import Controller from './Controller';
import { difference } from 'lodash';
import { Combined_CreateMethod } from '../commands/combined';
import type Report from '../checks/Report';
import type { IModelVersion } from './Controller';
import type { IMetaModel } from '../../stores/model/metamodel';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { IInheritance } from '../../stores/model/metamodel/inheritance';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { MethodProperties } from '../commands/types';

/**
 * Controller to add a method.
 */
export default class AddMethodController extends Controller {
  eventTypeId: string;
  objectTypeId: string;

  constructor(modelVersion: IModelVersion, eventTypeId: string, objectTypeId: string) {
    super(modelVersion);
    this.eventTypeId = eventTypeId;
    this.objectTypeId = objectTypeId;
  }

  // Utility for getting the metamodel
  get metamodel(): IMetaModel {
    return this.modelVersion.content!.metamodel;
  }

  // Utility function to calculate the default name for the method based on the
  // name of the owner event type
  get defaultName(): string {
    return this.eventTypeName.replace(/^EV/, 'ME');
  }

  // Utility function to find the name of the owner event type
  get eventTypeName(): string {
    const et = this.metamodel.getEventTypeById(this.eventTypeId);
    return et ? et.name : '';
  }

  // Utility function to find the name of the owner object type
  get objectTypeName(): string {
    const ot = this.metamodel.getObjectTypeById(this.objectTypeId);
    return ot ? ot.name : '';
  }

  /**
   * Find the methods that are acquirable for the owner event type and owner
   * object type combination. Returns a map of dependency -> list of acquirable
   * methods
   */
  get acquirableMethods(): Map<IDependency, IMethod[]> {
    const object = this.metamodel.getObjectTypeById(this.objectTypeId)!;
    // All viaMethods of the object type's acquired method have already been
    // acquired.
    const alreadyAcquired = object.acquiredMethods.map((m) => m.viaMethod);
    const result = new Map<IDependency, IMethod[]>();
    // Loop over all dependencies where the object type is the master to search
    // for the methods acquirable via that dependency.
    for (const dependency of object.dependenciesAsMaster) {
      // Find the methods of the dependent of the dependency for the event type
      // under consideration.
      let acquirable = dependency.dependent
        .methodsForEventType(this.eventTypeId)
        .filter((m) => !m.inherited);
      // Append the methods of the dependent's subtypes (except inherited)
      acquirable = acquirable.concat(
        dependency.dependent.indirectSubtypes
          .flatMap((st) => st.methodsForEventType(this.eventTypeId).slice())
          .filter((m) => !m.inherited)
      );
      // Filter out the already acquired ones. `alreadyAcquired` is typed as
      // `(IMethod | null)[]` because `viaMethod` is nullable in general, but an
      // acquired method always carries a non-null `viaMethod`; the cast narrows
      // to the difference's element type without changing the runtime set.
      acquirable = difference(acquirable, alreadyAcquired as IMethod[]);
      // Are there any left now?
      if (acquirable.length > 0) {
        // Yes: add them to the result
        result.set(dependency, acquirable);
      }
    }
    return result;
  }

  /**
   * Get the (single!) inheritance as subtype for the owner object.
   */
  get inheritanceAsSubtype(): IInheritance | undefined {
    const object = this.metamodel.getObjectTypeById(this.objectTypeId)!;
    if (object.inheritancesAsSubtype.length > 0) {
      return object.inheritancesAsSubtype[0];
    } else {
      return undefined;
    }
  }

  /**
   * Find the inheritable methods for the owner event type and owner object type combination.
   * Returns a list of inheritable methods.
   */
  get inheritableMethods(): IMethod[] {
    if (this.inheritanceAsSubtype != null) {
      const object = this.metamodel.getObjectTypeById(this.objectTypeId)!;
      const supertype = this.inheritanceAsSubtype.supertype;
      const alreadyInherited = object.inheritedMethods.map((im) => im.viaMethod);
      return supertype.methods.filter((m) => !m.specialised && !alreadyInherited.includes(m));
    } else {
      return [];
    }
  }

  /**
   * Find the methods that are specialisable for the owner event type and owner object type
   * combination. Returns a list of specialisable methods.
   */
  get specialisableMethods(): IMethod[] {
    const object = this.metamodel.getObjectTypeById(this.objectTypeId)!;
    const event = this.metamodel.getEventTypeById(this.eventTypeId)!;
    // All inherited methods for the owner event type of the event type can be specialised, except
    // the ones that are already specialised
    if (event.specialised) {
      return object
        .inheritedMethodsForEventType(event.ownerEventType!.id)
        .filter((m) => !m.specialised);
    } else {
      return [];
    }
  }

  /**
   * Add a new method
   *
   * @param {Object} properties method properties. See CCMC_Method.
   * @param {Boolean} propagate should the method be propagated automatically?
   */
  async addMethod(properties: MethodProperties, propagate: boolean): Promise<Report> {
    return this.executeAndCheck((model) => {
      const { checks } = Combined_CreateMethod(model, properties, propagate);
      return checks;
    });
  }
}
